import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, Briefcase, ClipboardCheck, HardHat, ShieldAlert,
  ShieldCheck, Users, BarChart3, CheckCircle2, CircleDot, Award, Quote,
  MapPin, Building2, Route
} from 'lucide-react';
import { metrics, services, backgroundWriteups, testimonial, capabilities, partners } from '../mock';
import Reveal from '../components/Reveal';
import SectionLabel from '../components/SectionLabel';

const iconMap = { ClipboardCheck, HardHat, ShieldAlert, ShieldCheck, Users, BarChart3 };

const Hero = () => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 bg-grid opacity-60" />
    <div className="absolute inset-0 bg-radial-glow" />
    <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-orange-500/[0.06] blur-3xl float-y" />
    <div className="absolute -bottom-32 -left-16 w-[420px] h-[420px] rounded-full bg-emerald-500/[0.05] blur-3xl" />

    <div className="relative max-w-[1320px] mx-auto px-6 pt-24 pb-20 flex flex-col items-center text-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="inline-flex items-center gap-3 rounded-full border border-emerald-400/25 bg-emerald-400/[0.06] pl-2 pr-4 py-1.5"
      >
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-2.5 py-1 text-[11px] font-mono tracking-[0.2em] text-emerald-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> CHAINAGE ADVISORY
        </span>
        <span className="text-slate-400">•</span>
        <span className="text-xs text-slate-300">Independent Infrastructure &amp; Road Asset Advisory</span>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="mt-8 font-serif text-white text-[48px] md:text-[64px] leading-[1.05] tracking-tight max-w-5xl"
      >
        Specialist infrastructure advisory, built on operational experience —{' '}
        <span className="italic text-orange-400 text-[36px] md:text-[48px] align-baseline">not just consulting theory.</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.25 }}
        className="mt-6 max-w-2xl text-slate-400 text-lg leading-relaxed"
      >
        Helping Australian infrastructure agencies close the gap between contract terms and contractor delivery.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.35 }}
        className="mt-9 flex flex-wrap gap-3 justify-center"
      >
        <Link to="/experience" className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-[#0a1120] font-semibold text-sm px-5 py-3 rounded-md transition-colors">
          VIEW OPERATIONAL BACKGROUND <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
        <Link to="/contact" className="inline-flex items-center gap-2 border border-white/15 hover:border-orange-400/60 text-slate-200 hover:text-orange-300 font-medium text-sm px-5 py-3 rounded-md transition-colors">
          REQUEST ADVISORY PROPOSAL
        </Link>
      </motion.div>

      <div className="mt-14 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full max-w-5xl text-left">
        {metrics.map((m, i) => (
          <motion.div
            key={m.label}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 + i * 0.08 }}
            className="relative rounded-lg border border-white/8 bg-white/[0.02] p-5 hover:border-orange-400/40 hover:bg-white/[0.04] transition-all group"
          >
            <div className={`font-serif text-3xl ${m.highlight ? 'text-orange-400' : 'text-white'}`}>{m.value}</div>
            <div className="text-sm text-slate-200 mt-1.5 font-medium">{m.label}</div>
            <div className="text-[11px] font-mono tracking-wider text-slate-500 mt-1">{m.sub}</div>
            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const ValueProp = () => (
  <section className="relative py-20">
    <div className="max-w-[1320px] mx-auto px-6">
      <Reveal>
        <SectionLabel icon={Briefcase}>OPERATIONAL EXPERIENCE &amp; VALUE PROPOSITION</SectionLabel>
      </Reveal>

      <div className="grid lg:grid-cols-3 gap-8 mt-6">
        <Reveal className="lg:col-span-2">
          <h2 className="font-serif text-white text-4xl md:text-5xl leading-tight tracking-tight max-w-2xl">
            Bridging Field Operations with Government Contract Governance
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed max-w-3xl">
            Many advisory firms offer theoretical framework reviews detached from site realities. Chainage Advisory was established to provide state road authorities and Tier-1 consulting partners with direct operational expertise — leveraging real-world experience in contract KPI enforcement, traffic guidance auditing, and multi-million dollar asset renewal execution.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {[
              { title: 'Independent Field Verification', desc: 'Ensuring contractor payment claims align strictly with physical site conditions and technical standards.' },
              { title: 'Audit-Defensible Records', desc: 'Preparing thorough documentation for treasury, parliamentary, and internal quality audits.' },
            ].map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }}
                className="rounded-lg border border-white/8 bg-white/[0.02] p-5 hover-ring transition-all"
              >
                <div className="flex items-center gap-2 text-sm text-white font-medium">
                  <CheckCircle2 className="w-4 h-4 text-orange-400" /> {it.title}
                </div>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{it.desc}</p>
              </motion.div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-6">
            <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300 mb-5">OPERATIONAL CREDENTIALS</div>
            <ul className="space-y-4">
              {capabilities.map((c) => (
                <li key={c.key} className="flex gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                  <div>
                    <div className="text-sm text-white font-medium">{c.key}</div>
                    <div className="text-xs text-slate-400 mt-1 leading-relaxed">{c.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/about" className="mt-6 inline-flex items-center justify-center gap-2 w-full py-2.5 border border-white/15 hover:border-orange-400/60 rounded-md text-sm text-slate-200 hover:text-orange-300 transition-colors">
              Read Founder Profile <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesGrid = () => (
  <section className="relative py-20 border-t border-white/5">
    <div className="max-w-[1320px] mx-auto px-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <Reveal>
          <SectionLabel icon={Route}>ADVISORY CAPABILITIES</SectionLabel>
          <h2 className="mt-4 font-serif text-white text-4xl md:text-5xl leading-tight tracking-tight">
            Specialised Technical Services
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/services" className="group inline-flex items-center gap-2 text-orange-300 hover:text-orange-200 text-sm font-medium">
            View All Service Scopes &amp; Deliverables <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-10 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {services.map((s, i) => {
          const Icon = iconMap[s.icon] || ClipboardCheck;
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              whileHover={{ y: -4 }}
              className="group relative rounded-xl border border-white/8 bg-[#0f1a2f]/60 p-6 hover-ring transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-orange-400 group-hover:border-orange-400/40 transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-serif text-4xl text-slate-700 group-hover:text-orange-400/40 transition-colors">{s.id}</div>
              </div>
              <div className="mt-4 text-[10px] font-mono tracking-[0.2em] uppercase text-orange-300/90 px-2 py-1 rounded bg-orange-500/10 border border-orange-500/20 inline-block">
                {s.category}
              </div>
              <h3 className="mt-4 font-serif text-white text-xl leading-snug">{s.title}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.description}</p>
              <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between">
                <Link to="/services" className="text-sm font-medium text-orange-300 hover:text-orange-200 inline-flex items-center gap-1.5">
                  Explore Scope &amp; Deliverables <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <span className="text-[10px] font-mono text-slate-500">Ref: {s.ref}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const TrackRecord = () => {
  const featured = backgroundWriteups.find((w) => w.featured);
  return (
    <section className="relative py-20 border-t border-white/5">
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <Reveal>
            <SectionLabel icon={Award}>OPERATIONAL TRACK RECORD</SectionLabel>
            <h2 className="mt-4 font-serif text-white text-4xl md:text-5xl leading-tight tracking-tight">
              Featured Background Write-Up
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/experience" className="group inline-flex items-center gap-2 text-orange-300 hover:text-orange-200 text-sm font-medium">
              Explore All 4 Background Write-Ups <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 rounded-xl border border-white/8 bg-gradient-to-br from-[#0f1a2f]/80 to-[#0a1120]/80 p-8 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="font-mono text-sm text-orange-300">{featured.title}</div>
              <div className="text-[11px] font-mono tracking-wider text-orange-300 px-3 py-1.5 rounded-md bg-orange-500/10 border border-orange-500/20">
                {featured.tag}
              </div>
            </div>
            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-[11px] font-mono tracking-[0.2em] text-slate-500">BACKGROUND</div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{featured.background}</p>
              </div>
              <div className="rounded-lg border-l-2 border-emerald-400/60 bg-emerald-400/[0.04] p-4">
                <div className="text-[11px] font-mono tracking-[0.2em] text-emerald-300">RESULT / GOVERNANCE IMPACT</div>
                <p className="mt-2 text-sm text-slate-200 leading-relaxed">{featured.result}</p>
              </div>
            </div>
            <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-end">
              <Link to={`/case-study/${featured.id}`} className="inline-flex items-center gap-1.5 text-sm text-orange-300 hover:text-orange-200 font-medium">
                View full case study <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const PartnersMarquee = () => (
  <section className="py-12 border-t border-white/5 overflow-hidden">
    <div className="max-w-[1320px] mx-auto px-6">
      <div className="text-[11px] font-mono tracking-[0.22em] text-slate-500 text-center">FRAMEWORKS · STANDARDS · AGENCIES ALIGNED</div>
    </div>
    <div className="relative mt-6">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0a1120] to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0a1120] to-transparent z-10" />
      <div className="flex marquee-track gap-14 w-max">
        {[...partners, ...partners].map((p, i) => (
          <div key={i} className="flex items-center gap-3 text-slate-400 font-mono text-sm tracking-wider">
            <CircleDot className="w-3.5 h-3.5 text-orange-400/70" /> {p}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Testimonial = () => (
  <section className="relative py-20 border-t border-white/5">
    <div className="max-w-[1320px] mx-auto px-6">
      <Reveal>
        <div className="relative rounded-2xl border border-white/8 bg-gradient-to-br from-[#0f1a2f]/70 to-[#0a1120]/70 p-10 md:p-14 overflow-hidden">
          <Quote className="absolute right-8 top-8 w-24 h-24 text-white/5" strokeWidth={1} />
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-500/10 px-3 py-1.5 text-[11px] font-mono tracking-[0.22em] text-orange-300">
            <Award className="w-3.5 h-3.5" /> PEER ATTESTATION &amp; INDUSTRY FEEDBACK
          </div>
          <p className="mt-8 font-serif italic text-white text-2xl md:text-3xl leading-relaxed max-w-4xl text-center mx-auto">
            “{testimonial.quote}”
          </p>
          <div className="mt-8 flex flex-col items-center gap-1">
            <div className="w-12 h-px bg-orange-400/50" />
            <div className="mt-3 text-white font-medium">{testimonial.author}</div>
            <div className="text-sm text-slate-400">{testimonial.company}</div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const CTA = () => (
  <section className="relative py-16 border-t border-white/5">
    <div className="max-w-[1320px] mx-auto px-6">
      <Reveal>
        <div className="rounded-2xl border border-white/8 bg-[#0f1a2f]/60 p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="font-serif text-white text-2xl md:text-3xl leading-snug">
              Discuss an Infrastructure Advisory Engagement
            </h3>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Available for contractor KPI auditing, road asset management reviews, and technical sub-consulting for state road agencies, local government, and Tier-1 consultancies across Australia.
            </p>
          </div>
          <Link to="/contact" className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-[#0a1120] font-semibold text-sm px-5 py-3 rounded-md transition-colors self-start md:self-center">
            REQUEST CONSULTATION <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  return (
    <div>
      <Hero />
      <ValueProp />
      <PartnersMarquee />
      <ServicesGrid />
      <TrackRecord />
      <Testimonial />
      <CTA />
    </div>
  );
};

export default Home;
